import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Alert,
    Spinner,
    Row,
    Col,
} from "reactstrap";
import { withNamespaces } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { useMutation } from "@tanstack/react-query";
import { uploadCSV } from "../../api/apiFunctions";
import { connect } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import Papa from "papaparse";
import { toast } from "react-toastify";
import StringUtils from "src/services/utils/StringUtils";

const FailedImportUsersList = ({ t, errors }) => {
    const { ExportCSVButton } = CSVExport;

    const stringUtils = new StringUtils();

    const tableColumns = [
        {
            key: 1,
            dataField: "salutation",
            text: t("Salutation"),
            sort: true,
            style: {
                width: '60px'
            }
        },
        {
            key: 2,
            dataField: "firstName",
            text: t("First Name"),
            sort: true,
            style: {
                width: '120px'
            }
        },
        {
            key: 3,
            dataField: "lastName",
            text: t("Last Name"),
            sort: true,
            style: {
                width: '120px'
            }
        },
        {
            key: 4,
            dataField: "email",
            text: t("Email"),
            style: {
                width: "200px",
            },
            sort: true,
        },
        {
            key: 5,
            dataField: "position",
            style: {
                width: "80px"
            },
            hidden: true,
            text: t("Position"),
            sort: true
        },
        {
            key: 6,
            dataField: "role",
            hidden: true,
            text: t("Role"),
            sort: true
        },
        {
            key: 7,
            dataField: "department",
            hidden: true,
            text: t("Department"),
            sort: true
        },
        {
            key: 8,
            dataField: "phoneNumber",
            hidden: true,
            text: t("Office Number"),
            sort: true
        },
        {
            key: 9,
            dataField: "mobileNumber",
            hidden: true,
            text: t("Mobile Number"),
            sort: true
        },
        {
            key: 10,
            dataField: "errors",
            text: t("Errors"),
            sort: false,
            style: {
                width: "250px",
            },
            formatter: (cellContent) => {
                return (
                    <div>
                        {Object.entries(cellContent).map(([key, value]) => {
                            return (
                                <span className="d-block text-danger">
                                    {t(stringUtils.capitalizeFirstLetter(key))}: {t(value)}
                                </span>
                            )
                        })}
                    </div>
                )
            }
        }
    ];

    const exportColumns = tableColumns.filter(
        (col) => !["errors"].includes(col.dataField)
    );

    const exportData = errors.map((err) =>
        exportColumns.reduce((acc, col) => {
            acc[col.dataField] = err[col.dataField];
            return acc;
        }, {})
    );

    return (
        <div className="mt-3">
            <Alert 
                color="danger"
            >
                <strong>{t('Upload failed')}:</strong>
                <p>
                    {t('Some rows contain missing required fields or duplicate email addresses.')}
                </p>
            </Alert>

            <ToolkitProvider 
                keyField="id" 
                data={exportData}
                columns={exportColumns}
                exportCSV={{
                    fileName: "users_import_errors.csv",
                    exportAll: true
                }}
            >
                {(props) => (
                    <div>
                        <div className="d-flex justify-content-end mb-2">
                            <ExportCSVButton
                                className="btn btn-primary"
                                {...props.csvProps}
                            >
                                <i className="ri-download-2-line me-1"></i>
                                {t('Download Users with Errors')}
                            </ExportCSVButton>
                        </div>

                        <BootstrapTable
                            defaultSorted={[]}
                            keyField={"id"}
                            responsive
                            bordered={false}
                            striped={false}
                            wrapperClasses="table-responsive"
                            classes={"table tpdd-table"}
                            headerWrapperClasses={"thead-light"}
                            {...props.baseProps}
                            columns={tableColumns}
                            data={errors}
                        />
                    </div>
                )}
            </ToolkitProvider>
        </div>
    )
}

const UploadCSVModal = ({ t, isOpen, toggle, onUpload, modules, token }) => {
    const activeModule = modules?.active;

    const [errors, setErrors] = useState([]);

    const handleError = ({ response }) => {
        Papa.parse(elsePayload.variables.get('file'), {
            complete: (parsedData) => {
                const result = [];
                const errs = response?.data?.message || [];

                const findFieldIndex = (fieldName) => {
                    return (parsedData?.data[0] || []).findIndex((item) => {
                        return item.toLowerCase() === fieldName;
                    });
                }

                const emailIndex = findFieldIndex('email');

                if (emailIndex > -1) {
                    errs.forEach(err => {
                        const itemData = (parsedData?.data || []).find((item) => item[emailIndex] === err.email);

                        const salutationIndex = findFieldIndex('salutation');
                        const firstNameIndex = findFieldIndex('first name');
                        const lastNameIndex = findFieldIndex('last name');
                        const positionIndex = findFieldIndex('position');
                        const roleIndex = findFieldIndex('role');
                        const departmentIndex = findFieldIndex('department');
                        const findFirstFieldIndex = (...names) => {
                            for (const name of names) {
                                const idx = findFieldIndex(name);
                                if (idx > -1) return idx;
                            }
                            return -1;
                        };
                        const phoneNumberIndex = findFirstFieldIndex(
                            'office number',
                            'phone number',
                            'phone'
                        );
                        const mobileNumberIndex = findFirstFieldIndex(
                            'mobile number',
                            'mobile'
                        );

                        result.push({
                            salutation: salutationIndex > -1 ? itemData[salutationIndex] : '',
                            firstName: firstNameIndex > -1 ? itemData[firstNameIndex] : '',
                            lastName: lastNameIndex > -1 ? itemData[lastNameIndex] : '',
                            email: emailIndex > -1 ? itemData[emailIndex] : '', 
                            position: positionIndex > -1 ? itemData[positionIndex] : '',
                            role: roleIndex > -1 ? itemData[roleIndex] : '',
                            department: departmentIndex > -1 ? itemData[departmentIndex] : '',
                            phoneNumber: phoneNumberIndex > -1 ? itemData[phoneNumberIndex] : '',
                            mobileNumber: mobileNumberIndex > -1 ? itemData[mobileNumberIndex] : '',
                            errors: {
                                ...err?.error || {}
                            }
                        })
                    });
                }

                setErrors(result);
            },
            error: () => {
                toast(t("Error to parse the csv file"), {
                    type: "error"
                });
            }
        });
    }

    const {
        mutate: uploadCSVMutation,
        isLoading: isUploading,
        ...elsePayload
    } = useMutation({
        mutationFn: async (payload) => {
            await uploadCSV({
                activeModule,
				token: token,
                payload
            })
        },
        onError: handleError,
        onSuccess: () => {
            toast(t("Users imported successfully!"), {
                type: "success"
            });

            onUpload && onUpload();
        }
    });

    useEffect(() => {
        if (isOpen) {
            setErrors([]);
        }
    }, [ isOpen ]);

    const handleUpload = (e) => {
        const file = e.target.elements.csvFile.files[0];
        
        const formData = new FormData();
        formData.append('file', file);

        uploadCSVMutation(formData);
    };

    const displayErrors = errors.length > 0;

    return (
        <Modal size="xl" isOpen={isOpen} toggle={toggle}>
            <AvForm
                className="needs-validation"
                onValidSubmit={handleUpload}
                enctype="multipart/form-data"
            >
                <ModalHeader 
                    toggle={toggle}
                >
                    {t('Upload Users CSV')}
                </ModalHeader>

                <ModalBody>
                    <Row>
                        <Col sm="12" md="6">
                            <Label for="csvFile">
                                {t('Select CSV File')}
                            </Label>
                            <AvField
                                name="csvFile"
                                id="csvFile"
                                type="file"
                                className="form-control fileUpload"
                                accept=".csv"
                                validate={{
                                    required: {
                                        value: true
                                    }
                                }}
                            />
                        </Col>
                    </Row>

                    {displayErrors && (
                        <FailedImportUsersList
                            errors={errors}
                            t={t}
                        />
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button 
                        color="secondary" 
                        onClick={toggle} 
                        disabled={isUploading}
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        color="primary"
                        type="submit"
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <>
                                <Spinner
                                    size="sm"
                                    className="me-2"
                                /> {t('Uploading')}...
                            </>
                        ) : (
                            <>
                                <i className="ri-upload-2-line me-1" />
                                {t('Upload')}
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    );
}

const mapStatetoProps = (state) => {
	const { token } = state.Login;
	const { Modules } = state;
	return {
		token,
		modules: Modules,
	};
};

export default withNamespaces()(
    connect(mapStatetoProps)(UploadCSVModal)
)